import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { LDateTime } from '@app/components/date/LDateTime';
import { useToast } from '@app/core/notifications';
import { publishCardMessages, publishMessages } from '@app/modules/social-export/publish';
import { socialExport } from '@mi-front/api-clients';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Button, Stack } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

export function SocialUploadScheduledStatus({
  upload,
  scheduledData,
}: {
  upload: socialExport.UploadDto;
  scheduledData: socialExport.UploadScheduledDataDto;
}) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess($t(publishMessages.publicationDeleted));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(publishMessages.errorDeletingPublication));
      },
    },
  });

  const promoteMutation = socialExport.usePromoteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess($t(publishMessages.publicationStarted));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(publishMessages.errorPublishingNow));
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  return (
    <Alert severity="info" icon={<ScheduleSendIcon />}>
      <AlertTitle>
        <FormattedMessage {...publishMessages.scheduledAlertTitle} />
      </AlertTitle>
      <Box mt={1}>
        <FormattedMessage {...publishMessages.videoWillPublishAt} />{' '}
        <strong>
          <LDateTime value={scheduledData.publishAt} showRelative="inline" />
        </strong>
      </Box>
      <Box mt={2}>
        <Stack direction="row" spacing={2}>
          <ConfirmationAction
            title={$t(publishCardMessages.publishNowConfirmTitle)}
            message={<FormattedMessage {...publishCardMessages.publishNowConfirmMessage} />}
            confirmLabel={$t(publishCardMessages.publishNow)}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="primary"
            onConfirm={handlePromote}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onClick}
              >
                <FormattedMessage {...publishCardMessages.publishNow} />
              </Button>
            )}
          </ConfirmationAction>
          <ConfirmationAction
            title={$t(publishCardMessages.deleteScheduledPublishTitle)}
            message={<FormattedMessage {...publishCardMessages.deleteScheduledPublishMessage} />}
            confirmLabel={$t({
              id: 'common.delete',
              defaultMessage: 'Eliminar',
            })}
            cancelLabel={$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
            confirmColor="error"
            onConfirm={handleDelete}
          >
            {({ onClick }) => (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onClick}
              >
                <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
              </Button>
            )}
          </ConfirmationAction>
        </Stack>
      </Box>
    </Alert>
  );
}
